import React, { useState, useEffect, useContext, useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import { AuthContext } from "../../contexts/Auth/AuthContext";
import { getAllAccessLevels, getMenuPermissionsEditor, updateMenuPermissions } from "../../api/autenticoAPI";

function buildOverridesPayload(choices) {
  const out = {};
  Object.entries(choices).forEach(([id, mode]) => {
    if (mode === "allow") out[id] = true;
    else if (mode === "deny") out[id] = false;
  });
  return out;
}

export function MenuPermissionsPanel() {
  const { user, updateLocalUser } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [loadingList, setLoadingList] = useState(true);
  const [loadingEditor, setLoadingEditor] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editor, setEditor] = useState(null);
  const [choices, setChoices] = useState({});
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingList(true);
        const list = await getAllAccessLevels();
        if (!cancelled) setUsers(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!cancelled) {
          setSnack({ open: true, message: "Não foi possível carregar usuários.", severity: "error" });
        }
      } finally {
        if (!cancelled) setLoadingList(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setEditor(null);
      setChoices({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingEditor(true);
        const data = await getMenuPermissionsEditor(Number(selectedId));
        if (cancelled) return;
        setEditor(data);
        const next = {};
        (data.items || []).forEach((it) => {
          next[it.id] = it.override || "inherit";
        });
        setChoices(next);
      } catch (e) {
        if (!cancelled) {
          setEditor(null);
          setSnack({
            open: true,
            message: e?.response?.data?.error || "Erro ao carregar permissões.",
            severity: "error",
          });
        }
      } finally {
        if (!cancelled) setLoadingEditor(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const grouped = useMemo(() => {
    if (!editor?.items) return [];
    const map = new Map();
    editor.items.forEach((it) => {
      const g = it.group || "Outros";
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(it);
    });
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [editor]);

  const handleSave = async () => {
    if (!selectedId) return;
    try {
      setSaving(true);
      const menuOverrides = buildOverridesPayload(choices);
      const result = await updateMenuPermissions(Number(selectedId), menuOverrides);
      if (user?.id === Number(selectedId) && updateLocalUser) {
        updateLocalUser({ menuOverrides: result.menuOverrides || {} });
      }
      setSnack({ open: true, message: "Permissões salvas.", severity: "success" });
      const data = await getMenuPermissionsEditor(Number(selectedId));
      setEditor(data);
      const next = {};
      (data.items || []).forEach((it) => {
        next[it.id] = it.override || "inherit";
      });
      setChoices(next);
    } catch (e) {
      setSnack({
        open: true,
        message: e?.response?.data?.error || "Erro ao salvar.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto", py: 2, px: { xs: 1, sm: 2 } }}>
      <Typography variant="h6" gutterBottom>
        Permissões de menu por usuário
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        O <strong>nível</strong> do usuário define o padrão. Aqui você pode <strong>forçar permitir</strong> (acesso
        extra) ou <strong>forçar negar</strong> (bloquear algo que o nível permitiria). &quot;Padrão do nível&quot;
        remove a personalização para aquele item.
      </Typography>

      {loadingList ? (
        <CircularProgress size={28} />
      ) : (
        <FormControl fullWidth sx={{ mb: 2 }} size="small">
          <InputLabel id="perm-user-label">Usuário</InputLabel>
          <Select
            labelId="perm-user-label"
            label="Usuário"
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
          >
            <MenuItem value="">
              <em>Selecione…</em>
            </MenuItem>
            {users.map((u) => (
              <MenuItem key={u.id} value={String(u.id)}>
                {u.name} ({u.login}) — {u.level}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {loadingEditor && selectedId ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : null}

      {editor && !loadingEditor ? (
        <>
          {grouped.map(([groupName, items]) => (
            <Box key={groupName} sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                {groupName}
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Funcionalidade</TableCell>
                      <TableCell align="center">Padrão nível</TableCell>
                      <TableCell align="center">Efetivo</TableCell>
                      <TableCell sx={{ minWidth: 200 }}>Personalização</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Typography variant="body2">{item.label}</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {item.path}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">{item.baseline ? "Sim" : "Não"}</TableCell>
                        <TableCell align="center">{item.effective ? "Sim" : "Não"}</TableCell>
                        <TableCell>
                          <Select
                            size="small"
                            fullWidth
                            value={choices[item.id] || "inherit"}
                            disabled={saving}
                            onChange={(e) =>
                              setChoices((prev) => ({
                                ...prev,
                                [item.id]: e.target.value,
                              }))
                            }
                          >
                            <MenuItem value="inherit">Padrão do nível</MenuItem>
                            <MenuItem value="allow">Forçar permitir</MenuItem>
                            <MenuItem value="deny">Forçar negar</MenuItem>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ))}

          <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
            <Button variant="contained" onClick={handleSave} disabled={saving || !selectedId}>
              {saving ? "Salvando…" : "Salvar alterações"}
            </Button>
          </Box>
        </>
      ) : null}

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))} sx={{ width: "100%" }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
